import { Outlet, useNavigate, useParams } from 'react-router';
import { DataLengthTag, PresetTag } from '@components/display';
import { ISideBarProps, SideBar, TMenuBarItem } from '@components/navigation';
import { routes } from '@navigation/routes';
import { getStateKeyByValue } from '@utils';
import { CertificationState } from '@definitions';
import { useDelayedEffect, useAppTranslation } from '@hooks';
import { LoadingOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  colors,
  Content,
  Icon,
  LayoutContainer,
  ProgressIndicator,
  Row,
  Space,
  Title,
  Text,
} from 'common-components';
import { Loader } from '@components/utils';
import { PageLayout } from '@components/layout';
import { css } from '@emotion/react';

import { store } from '@/store';

const CertificationContainer = () => {
  const { id: idCertification } = useParams();

  const certification = store.use
    .certifications()
    ?.find((certification) => certification.idCertification === idCertification);

  const getCertificationAction = store.use.getCertificationAction().call;
  const updateStateCertificationAction = store.use.updateStateCertificationAction().call;

  const isLoadingUpdateStateCertificationAction =
    store.use.updateStateCertificationAction().status === 'LOADING';
  const isLoadingGetCertificationAction = store.use.getCertificationAction().status === 'LOADING';
  const isSucceededGetCertificationAction =
    store.use.getCertificationAction().status === 'SUCCEEDED';

  const navigate = useNavigate();
  const { translate } = useAppTranslation();

  const notPublishedStateKeys = Object.keys(CertificationState).filter(
    (state) =>
      state !== getStateKeyByValue(CertificationState, 'online') &&
      state !== getStateKeyByValue(CertificationState, 'archived'),
  );

  const isInEditState = notPublishedStateKeys.includes(certification?.state?.toString());
  const isOfflineCertification =
    certification?.state === getStateKeyByValue(CertificationState, 'offline');
  const isOnlineCertification =
    certification?.state === getStateKeyByValue(CertificationState, 'online');
  const isDraftCertification =
    certification?.state === getStateKeyByValue(CertificationState, 'extractionParameter') ||
    certification?.state === getStateKeyByValue(CertificationState, 'settingsDefinition') ||
    certification?.state === getStateKeyByValue(CertificationState, 'addData') ||
    certification?.state === getStateKeyByValue(CertificationState, 'addAnswers');

  const onSidebarClick: ISideBarProps['onClick'] = (e) => {
    navigate(e.key);
  };

  useDelayedEffect(() => {
    getCertificationAction({ idCertification });
  }, [idCertification]);

  const items: TMenuBarItem[] = (() => {
    const { state } = certification || {};
    const baseItems = [
      {
        key: routes.certificationQuestions.navigate(idCertification),
        icon: <Icon icon={'MessageQuestionSquare'} />,
        label: translate('certificationPage.title.questions'),
      },
      {
        key: routes.certificationRespondents.navigate(idCertification),
        icon: <Icon icon={'Users02'} />,
        label: translate('certificationPage.title.respondents'),
      },
      {
        key: routes.certificationParameters.navigate(idCertification),
        icon: <Icon icon={'Settings01'} />,
        label: translate('common.settings'),
        disabled: false,
      },
    ];

    if (state || !!isInEditState) {
      baseItems.splice(2, 0, {
        key: routes.certificationSegments.navigate(idCertification),
        icon: <Icon icon={'Target02'} />,
        label: translate('common.segments'),
      });
    }
    return baseItems;
  })();

  const getProgressPercentage = () => {
    const { nbQuestions = 1, nbPublishedQuestions = 1 } = certification || {};
    return (nbPublishedQuestions * 100) / nbQuestions;
  };

  const canPublish = certification?.nbPublishedQuestions >= certification?.nbQuestionsTest;
  const areAllQuestionsPublished =
    certification?.nbQuestions > 0 &&
    certification?.nbPublishedQuestions >= certification?.nbQuestions;

  const getProgressColor = () => {
    if (areAllQuestionsPublished) {
      return colors.success400;
    }
    if (canPublish) {
      return colors.primary600;
    }
    return colors.warning400;
  };

  return (
    <PageLayout
      css={css`
        margin: 0;
        max-width: none;
      `}
    >
      <Row
        style={{
          padding: '18px 16px',
          gap: 16,
          borderBottom: `1px solid ${colors.gray200}`,
        }}
      >
        <Button size="sm" onClick={() => navigate(routes.certifications.navigate())}>
          <Icon icon="XClose" size={20} />
        </Button>
        <Row justify={'space-between'} style={{ flex: 1 }}>
          <Space align={'center'} size={8}>
            <DataLengthTag>#{certification?.idCertification}</DataLengthTag>
            <Title preset="singlePageTitle">{certification?.name}</Title>
            <PresetTag preset={'certification-state'} state={certification?.state} withDot>
              {translate(`common.states.certificationState.${certification?.state}`)}
            </PresetTag>
          </Space>
          {isInEditState && (
            <Space align={'center'} size={16}>
              <ProgressIndicator
                percent={getProgressPercentage()}
                showInfo={false}
                size={{ width: 320, height: 8 }}
                railColor={colors.gray200}
                strokeColor={getProgressColor()}
              />
              <Text color={colors.gray500} style={{ fontWeight: 500 }}>
                <Text color={colors.gray800} style={{ fontWeight: 500 }}>
                  {translate('certificationPage.publishedCount', {
                    count: certification?.nbPublishedQuestions || 0,
                  })}
                </Text>
                {` / ${certification?.nbQuestions || 0} (min. ${certification?.nbQuestionsTest || 0})`}
              </Text>

              <Button
                {...(!isOnlineCertification ? { type: 'primary' } : {})}
                size="sm"
                onClick={() =>
                  updateStateCertificationAction({
                    idCertification: certification.idCertification,
                    isDraftCertification,
                    isOfflineCertification,
                    isOnlineCertification,
                  })
                }
                disabled={!canPublish}
              >
                <>
                  {isDraftCertification && `${translate('certificationPage.publishButtonLabel')} `}
                  {isOnlineCertification && `${translate('certificationPage.offlineButtonLabel')} `}
                  {isOfflineCertification && `${translate('certificationPage.onlineButtonLabel')} `}
                  {isLoadingUpdateStateCertificationAction && (
                    <LoadingOutlined style={{ marginLeft: 10 }} />
                  )}
                </>
              </Button>
            </Space>
          )}
        </Row>
      </Row>
      <LayoutContainer>
        <SideBar onClick={onSidebarClick} items={items} />
        <Content>
          <Col style={{ padding: '24px 32px' }}>
            {isLoadingGetCertificationAction && <Loader />}
            {isSucceededGetCertificationAction && <Outlet context={{ certification }} />}
          </Col>
        </Content>
      </LayoutContainer>
    </PageLayout>
  );
};

export default CertificationContainer;
